import { PartnershipMode } from 'core/agency/Agency';
import { CurrentRevenueSharing, formatPercentageText, RevenueSharingFormData } from 'core/revenueSharing/RevenueSharing';
import { DefaultRevenueSharingManager, RevenueSharingManager } from 'core/revenueSharing/RevenueSharingManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { isEmpty, omitBy } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { validateEmpty, validateMinMax, validateStringMaxLength } from 'utils/ValidateUtils';
import i18n from 'i18n';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const PURPOSE_MAX_LENGTH = 50;
export const PERCENTAGE_MAX_DECIMAL_PLACES = 2;
const percentagePattern = new RegExp(`^\\d+(\\.\\d{1,${PERCENTAGE_MAX_DECIMAL_PLACES}})?$`);

// 後端以 Asia/Taipei 判斷生效日是否早於今天，前端用同一個基準才不會在跨日時放行後端會擋的日期
export const getToday = () => moment.tz('Asia/Taipei').format(DATE_FORMAT);

export type RevenueSharingFormModel = {
  loading: boolean;
  redirect?: string;
  initRevenueSharing: RevenueSharingFormData;
  getOriginalPercentageText: (partnershipModeId: PartnershipMode) => string;
  validateForm: (values: RevenueSharingFormData) => { [key: string]: string };
  onSubmit: (values: RevenueSharingFormData) => void;
};

const defaultRevenueSharingManager: RevenueSharingManager = new DefaultRevenueSharingManager();
export const useRevenueSharingFormModel = (): RevenueSharingFormModel => {

  const { loading, callAPIs } = useCallAPI();
  const [redirect, setRedirect] = useState<string>();
  const [currentRevenueSharings, setCurrentRevenueSharings] = useState<CurrentRevenueSharing[]>([]);
  const { state } = useLocation<{ partnershipModeId?: PartnershipMode } | undefined>();
  const initPartnershipModeId = state?.partnershipModeId ?? PartnershipMode.SELF_SERVING;

  useEffect(() => {
    callAPIs(
      [() => defaultRevenueSharingManager.getCurrentRevenueSharings()],
      (records: CurrentRevenueSharing[]) => {
        setCurrentRevenueSharings(records);
      }
    );
  }, [callAPIs]);

  const getOriginalPercentageText = useCallback((partnershipModeId: PartnershipMode) => {
    const current = currentRevenueSharings.find(record => record.partnershipModeId === partnershipModeId);
    return current ? formatPercentageText(current.percentage) : '-';
  }, [currentRevenueSharings]);

  const validatePurpose = (purpose: string) => {
    if (purpose.trim().length === 0) {
      return i18n.t<string>('formValidate.labels.emptyError');
    }
    return validateStringMaxLength(
      purpose,
      PURPOSE_MAX_LENGTH,
      i18n.t<string>('revenueSharingForm.errors.purposeLength')
    );
  };

  const validateEffectiveDate = (isInstant: boolean, effectiveDate: string) => {
    if (isInstant) {
      return;
    }
    const error = validateEmpty(effectiveDate);
    if (error) {
      return error;
    }
    return effectiveDate < getToday()
      ? i18n.t<string>('revenueSharingForm.errors.effectiveDateBeforeToday')
      : undefined;
  };

  const validatePercentage = (percentage: number | string) => {
    const error = validateMinMax(percentage, 0, 100);
    if (error) {
      return error;
    }
    return percentagePattern.test(String(percentage).trim())
      ? undefined
      : i18n.t<string>('revenueSharingForm.errors.percentageDecimalPlaces', { places: PERCENTAGE_MAX_DECIMAL_PLACES });
  };

  const validateForm = useCallback((values: RevenueSharingFormData) => {
    return omitBy(
      {
        purpose: validatePurpose(values.purpose),
        effectiveDate: validateEffectiveDate(values.isInstant, values.effectiveDate),
        percentage: validatePercentage(values.percentage)
      },
      isEmpty
    ) as { [key: string]: string };
  }, []);

  const onSubmit = useCallback((values: RevenueSharingFormData) => {
    callAPIs([() => defaultRevenueSharingManager.createRevenueSharing(values)], () => {
      setRedirect('/revenue-sharings');
    });
  }, [callAPIs]);

  const initRevenueSharing: RevenueSharingFormData = useMemo(() => ({
    purpose: '',
    effectiveDate: getToday(),
    isInstant: false,
    partnershipModeId: initPartnershipModeId,
    percentage: ''
  }), [initPartnershipModeId]);

  return {
    loading,
    redirect,
    initRevenueSharing,
    getOriginalPercentageText,
    validateForm,
    onSubmit
  };
};
